import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../lib/utils';
import type {
  SelectLegacyGroupProps,
  SelectLegacyTriggerProps,
  SelectLegacyScrollUpButtonProps,
  SelectLegacyScrollDownButtonProps,
  SelectLegacyContentProps,
  SelectLegacyLabelProps,
  SelectLegacyItemProps,
  SelectLegacySeparatorProps,
} from './types';

const Select = SelectPrimitive.Root;

const SelectLegacyGroup = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Group>,
  SelectLegacyGroupProps
>(({ ...props }, ref) => (
  <SelectPrimitive.Group ref={ref} className='' {...props} />
));
SelectLegacyGroup.displayName = SelectPrimitive.Group.displayName;

const SelectLegacyValue = SelectPrimitive.Value;

const SelectLegacyTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  SelectLegacyTriggerProps
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'eye:h-9 eye:text-sm eye:flex eye:items-center eye:data-[placeholder]:text-grey-placeholder' +
        ' eye:whitespace-nowrap eye:focus:outline-none eye:disabled:cursor-not-allowed eye:[&>span]:line-clamp-1' +
        ' eye:cursor-pointer',
      className,
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectLegacyTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectLegacyScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  SelectLegacyScrollUpButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'eye:flex eye:cursor-default eye:items-center eye:justify-center eye:border-b eye:border-grey-20 eye:py-1',
      className,
    )}
    {...props}
  >
    <ChevronUp />
  </SelectPrimitive.ScrollUpButton>
));
SelectLegacyScrollUpButton.displayName =
  SelectPrimitive.ScrollUpButton.displayName;

const SelectLegacyScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  SelectLegacyScrollDownButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'eye:flex eye:cursor-default eye:items-center eye:justify-center eye:border-t eye:border-grey-20 eye:py-1',
      className,
    )}
    {...props}
  >
    <ChevronDown />
  </SelectPrimitive.ScrollDownButton>
));
SelectLegacyScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectLegacyContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  SelectLegacyContentProps
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'eye:text-popover-foreground eye:relative eye:z-[10001] eye:bg-white eye:py-2xs',
        'eye:min-w-[8rem] eye:origin-[--radix-select-content-transform-origin] eye:overflow-x-hidden',
        'eye:rounded-md eye:border eye:border-grey-20 eye:data-[side=bottom]:slide-in-from-top-2',
        'eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2',
        'eye:data-[side=top]:slide-in-from-bottom-2 eye:data-[state=closed]:animate-out',
        'eye:data-[state=closed]:fade-out-0 eye:data-[state=closed]:zoom-out-95',
        'eye:data-[state=open]:animate-in eye:data-[state=open]:fade-in-0 eye:data-[state=open]:zoom-in-95',
        position === 'popper' &&
          'eye:data-[side=bottom]:translate-y-1 eye:data-[side=left]:-translate-x-1 eye:data-[side=right]:translate-x-1 eye:data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <div
        className={cn(
          'eye:max-h-200 eye:overflow-y-auto',
          position === 'popper' &&
            'eye:w-full eye:min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </div>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectLegacyContent.displayName = SelectPrimitive.Content.displayName;

const SelectLegacyLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  SelectLegacyLabelProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('eye:py-1.5 eye:px-8 eye:font-semibold', className)}
    {...props}
  />
));
SelectLegacyLabel.displayName = SelectPrimitive.Label.displayName;

const SelectLegacyItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  SelectLegacyItemProps
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'eye:body-m eye:focus:bg-accent eye:flex eye:min-h-48 eye:cursor-pointer eye:items-center eye:px-[26px]',
      'eye:focus:text-accent-foreground eye:relative eye:w-full eye:py-5xs',
      'eye:outline-none eye:select-none eye:data-[disabled]:pointer-events-none eye:data-[disabled]:opacity-50',
      className,
      'eye:data-[highlighted]:bg-grey-10',
      'eye:data-[state=checked]:bg-grey-20',
    )}
    {...props}
  >
    <span className='eye:right-2 eye:h-3.5 eye:w-3.5 eye:absolute eye:flex eye:items-center eye:justify-center'>
      <SelectPrimitive.ItemIndicator>
        <Check className='eye:h-4 eye:w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectLegacyItem.displayName = SelectPrimitive.Item.displayName;

const SelectLegacySeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  SelectLegacySeparatorProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('eye:bg-muted eye:-mx-1 eye:my-1 eye:h-px', className)}
    {...props}
  />
));
SelectLegacySeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectLegacyGroup,
  SelectLegacyValue,
  SelectLegacyTrigger,
  SelectLegacyContent,
  SelectLegacyLabel,
  SelectLegacyItem,
  SelectLegacySeparator,
  SelectLegacyScrollUpButton,
  SelectLegacyScrollDownButton,
};
